import Button from '@/common/components/Button'
import Checkbox from '@/common/components/Checkbox'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import { useTefInputModes } from '@/common/hooks/queries/useTefInputModes'
import { TefInputModeData } from '@/common/types/tefInputMode.type'
import { CompanyData } from '@/features/companies/types/companies.type'
import { useUpdateTefInputMode } from '@/features/privateCompanies/hooks/politics/useUpdateTefInputMode'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

interface UpdateTefInputModeModalProps {
  company?: CompanyData
}

export const UpdateTefInputModeModal = ({ company }: UpdateTefInputModeModalProps) => {
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const [selectedTefInputModes, setSelectedTefInputModes] = useState<{ tef_input_mode_id: string }[]>([])
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { data: tefInputModes, isFetched: initialTefInputModesIsFetched } = useTefInputModes<TefInputModeData[]>({})

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['company', company?.id] })
    setIsOpen(false)
  }

  const { mutate: updateTefInputMode, isPending: updateTefInputModeIsPending } = useUpdateTefInputMode({
    companyId: company?.id,
    clientCompanyTefInputModes: company?.client_company_tef_input_modes || [],
    onSuccess,
  })

  useEffect(() => {
    if (initialTefInputModesIsFetched) {
      setSelectedTefInputModes(
        company?.client_company_tef_input_modes?.map((mode) => ({ tef_input_mode_id: mode.tef_input_mode.id })) || [],
      )
    }
  }, [initialTefInputModesIsFetched])

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={() => updateTefInputMode(selectedTefInputModes)}
      Trigger={
        <Button
          name="open_update_tef_input_mode_modal"
          operationKlassToCheckPermission="Admin::Client::Companies::UpdateTefInputs"
          className="h-[2.125rem] text-sm text-white-800"
        >
          Editar
        </Button>
      }
      title="Editando TEFs"
      dataTestid="update_tef_input_mode"
      useCancellableFooter
      confirmButtonClassName="bg-blue-700"
      disabled={updateTefInputModeIsPending}
      confirmButtonIsLoading={updateTefInputModeIsPending}
      body={
        <>
          <form className="flex flex-col gap-y-4 text-white-800">
            {tefInputModes?.map((tefInputMode) => (
              <Checkbox
                key={tefInputMode.id}
                label={tefInputMode.display_name}
                checked={
                  selectedTefInputModes.find((value) => value.tef_input_mode_id === tefInputMode.id) ? true : false
                }
                onChange={(e) => {
                  if ((e.target as HTMLInputElement).checked) {
                    setSelectedTefInputModes([...selectedTefInputModes, { tef_input_mode_id: tefInputMode.id }])
                  } else {
                    setSelectedTefInputModes(
                      selectedTefInputModes.filter((value) => value.tef_input_mode_id !== tefInputMode.id),
                    )
                  }
                }}
              />
            ))}
          </form>

          {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
        </>
      }
    />
  )
}
